import { useState, useEffect, useCallback } from 'react'
import { fetchRounds, type RoundState } from '@/api/rounds'
import type { Round } from '@/types/bracket'

const ROUND_ORDER: Round[] = ['R32', 'R16', 'QF', 'SF', '3RD', 'FINAL']
const POLL_INTERVAL_MS = 30_000

// Round-aware replacement for the old single-global-lockAt countdown.
// Pulls per-round state from GET /rounds instead of one timestamp.
//
// - useLockStatus()       -> tracks the current "active" round (the first
//                             round in tournament order that isn't SCORED
//                             yet), for the overall bracket countdown.
// - useLockStatus('R32')  -> tracks a specific round, e.g. for the
//                             Champion Pick page, which closes with R32.
export function useLockStatus(round?: Round) {
  const [rounds, setRounds] = useState<RoundState[]>([])
  const [isLocked, setIsLocked] = useState(false)
  const [secondsLeft, setSecondsLeft] = useState(0)
  const [lockAt, setLockAt] = useState(0)
  const [activeRound, setActiveRound] = useState<Round | null>(null)

  const load = useCallback(() => {
    fetchRounds()
      .then(setRounds)
      .catch(() => {
        // Keep last-known state on transient fetch errors.
      })
  }, [])

  useEffect(() => {
    load()
    const id = setInterval(load, POLL_INTERVAL_MS)
    return () => clearInterval(id)
  }, [load])

  useEffect(() => {
    function tick() {
      if (rounds.length === 0) return

      const target = round
        ? rounds.find(r => r.round === round)
        : rounds.find(r => r.status !== 'SCORED') ?? rounds[rounds.length - 1]

      if (!target) {
        setIsLocked(false)
        setSecondsLeft(0)
        setLockAt(0)
        setActiveRound(null)
        return
      }

      setActiveRound(target.round)

      const now = Math.floor(Date.now() / 1000)
      const effectiveLockAt = target.effective_lock_at ?? 0
      setLockAt(effectiveLockAt)

      // isLocked only when the round is no longer open (admin-driven: PENDING/SCORED).
      // Per-match locking (30 min before kickoff) is handled in BracketTree, not here.
      if (!target.is_open_for_submission) {
        setIsLocked(true)
        setSecondsLeft(0)
        return
      }

      setIsLocked(false)
      // secondsLeft is kept for legacy callers; Bracket.tsx uses secsUntilNextLock instead.
      const diff = effectiveLockAt > 0 ? effectiveLockAt - now : 0
      setSecondsLeft(Math.max(0, diff))
    }

    tick()
    const id = setInterval(tick, 1000)
    return () => clearInterval(id)
  }, [rounds, round])

  return { isLocked, secondsLeft, lockAt, activeRound, rounds, refresh: load }
}

export function formatCountdown(seconds: number): string {
  if (seconds <= 0) return '—'
  const d = Math.floor(seconds / 86400)
  const h = Math.floor((seconds % 86400) / 3600)
  const m = Math.floor((seconds % 3600) / 60)
  const s = seconds % 60
  if (d > 0) return `${d}d ${h}h ${m}m`
  if (h > 0) return `${h}h ${m}m ${s}s`
  return `${m}m ${s}s`
}

export { ROUND_ORDER }
